import {
    Box,
    Card,
    CardBody,
    CardHeader,
    Flex,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { getCoreRowModel, PaginationState, SortingState, useReactTable } from '@tanstack/react-table';
import { isPlainObject } from 'lodash';
import { useMemo, useState } from 'react';
import { BiMenu, BiPencil, BiShareAlt, BiTrash, BiWindowOpen } from 'react-icons/bi';
import { Link as RouterLink } from 'react-router-dom';

import { copyText } from 'utils/dom-utils';

import { useRemoveDashboardWidget } from '../../api/dashboardWidget';
import { PageSpinner } from '../../widgets/PageSpinner';
import { AsOfDateTag } from '../../widgets/Table/AsOfDateTag';
import { Pagination } from '../../widgets/Table/Pagination';
import { Table } from '../../widgets/Table/Table';
import { WidgetWizard } from '../../widgets/WidgetWizard/WidgetWizard';
import { getColumnDefinitions, getPageUrl, getQueryFn, parseFilter } from './widgetUtils';

export const DashboardWidget = ({
    widgetDefinition,
    isCustom,
}: {
    widgetDefinition: DashboardWidgetDefinition;
    isCustom?: boolean;
}) => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { mutate: removeWidget } = useRemoveDashboardWidget();

    const { id, type, filters, queryMeta, columnIds, sort, title } = widgetDefinition;

    const [sortState, setSortState] = useState<SortingState>(sort || []);
    const [paginationState, setPaginationState] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 25,
    });

    const queryFn = getQueryFn(type);
    const columnFilters = parseFilter(filters);
    const { data, isFetching, isLoading } = queryFn({
        pagination: paginationState,
        sorting: sortState,
        columnFilters,
        ...queryMeta,
    });

    const tableData = useMemo(() => {
        if (!data) {
            return [];
        }

        if ('portfolios' in data) {
            return data.portfolios;
        }
        if ('positions' in data) {
            return data.positions;
        }
        if ('assets' in data) {
            return data.assets;
        }
        if ('withdrawals' in data) {
            return data.withdrawals;
        }
        if ('growthAllocation' in data) {
            return data.growthAllocation;
        }

        return [];
    }, [data]);

    const columns = useMemo(() => getColumnDefinitions(type, columnIds), [columnIds, type]);
    const table = useReactTable({
        data: tableData,
        columns,
        defaultColumn: {
            enableColumnFilter: false,
        },
        state: {
            pagination: paginationState,
            sorting: sortState,
        },
        onPaginationChange: setPaginationState,
        onSortingChange: setSortState,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    const externalUrl = useMemo(() => {
        let qs = columnFilters
            .map((filter) => {
                if (Array.isArray(filter.value)) {
                    return filter.value.map((val) => `${filter.id}=${val}`);
                }
                return `${filter.id}=${isPlainObject(filter.value) ? encodeURIComponent(JSON.stringify(filter.value)) : (filter.value as string)}`;
            })
            .flat()
            .join('&');

        if (queryMeta?.dataVariant) {
            qs += `&dataVariant=${queryMeta?.dataVariant as string}`;
        }

        return `${getPageUrl(type)}?${qs}`;
    }, [columnFilters, queryMeta?.dataVariant, type]);

    return (
        <Card w="100%">
            <CardHeader px={5} py={3} borderBottomWidth={1}>
                <Flex justify="space-between" alignItems="center">
                    <Text size="sm" fontWeight="medium" flexShrink={0}>
                        {title}
                    </Text>
                    <Flex alignItems="center" gap={2}>
                        <AsOfDateTag dateString={data?.asOfDate} />

                        {isCustom && (
                            <Menu>
                                <MenuButton
                                    size="xs"
                                    as={IconButton}
                                    aria-label="Options"
                                    icon={<Icon boxSize={4} as={BiMenu} />}
                                    variant="outline"
                                />
                                <MenuList>
                                    <MenuItem icon={<Icon boxSize={4} as={BiPencil} />} onClick={onOpen}>
                                        Edit
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiShareAlt} />}
                                        onClick={async () => {
                                            const didCopy = await copyText(
                                                `Use this string to import the "${title}" widget on CIP:\n${btoa(
                                                    JSON.stringify(widgetDefinition),
                                                )}`,
                                            );
                                            toast({
                                                title: didCopy ? 'Ready to share!' : 'Unable to copy widget details.',
                                                status: didCopy ? 'info' : 'error',
                                                duration: 8000,
                                            });
                                        }}
                                    >
                                        Share
                                    </MenuItem>
                                    <MenuItem icon={<Icon boxSize={4} as={BiTrash} />} onClick={() => removeWidget(id)}>
                                        Remove
                                    </MenuItem>
                                </MenuList>
                            </Menu>
                        )}

                        <IconButton
                            size="xs"
                            aria-label="open"
                            icon={<Icon boxSize={4} as={BiWindowOpen} />}
                            variant="outline"
                            as={RouterLink}
                            to={externalUrl}
                        />
                    </Flex>
                </Flex>
            </CardHeader>
            <CardBody p={3}>
                {isLoading ? (
                    <PageSpinner />
                ) : !!data?.totalNr && data?.totalNr > 0 ? (
                    <>
                        <Table table={table} isLoading={isFetching} allowXOverflow />
                        {data.totalNr >= 20 && (
                            <Box px={2}>
                                <Pagination table={table} totalItems={data.totalNr} />
                            </Box>
                        )}
                    </>
                ) : (
                    <Text px={2}>
                        <i>No results found</i>
                    </Text>
                )}
            </CardBody>
            {isOpen && <WidgetWizard onClose={onClose} initialValue={widgetDefinition} showImport={false} />}
        </Card>
    );
};
